<template>
  <div>
    <table-pagination class="mb-1" :pagination="pagination" :counter-label="$t('backup.tabs.trades')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <b-table responsive class="bk-table" :items="rows" :fields="fields"
             no-local-sorting :sort-by="sort.field" :sort-desc="sortIsDesc"
             :busy="loading" hover show-empty :tbody-tr-class="rowClass"
             @sort-changed="onSortChanged" @row-clicked="onRowClick">
      <template #table-busy>
        <div class="text-center text-primary my-2"><b-spinner small class="mr-50"/>{{ $t('backup.loading') }}</div>
      </template>
      <template #empty>
        <backup-table-state :error="loadError ? errorText() : null" icon="fa-solid fa-chart-line"
                            :title="$t('backup.emptyTrades')" :hint="$t('backup.emptyHint')" @retry="refresh"/>
      </template>

      <template #head(select)>
        <b-form-checkbox :checked="allPagePicked" :indeterminate="somePagePicked"
                         :disabled="!selectableRows.length" @change="togglePagePick"/>
      </template>
      <template #cell(select)="{ item }">
        <span v-b-tooltip.hover.right="selectBlockReason(item)">
          <b-form-checkbox class="no-click" :checked="isPicked(item)" :disabled="!isSelectable(item)"
                           @change="v => togglePick(item, v)"/>
        </span>
      </template>

      <template #cell(symbol)="{ item }">
        <div class="bk-two">
          <span class="font-weight-bold">{{ item.symbol }}
            <span class="bk-side" :class="isBuy(item) ? 'bk-side--buy' : 'bk-side--sell'">{{ item.side }}</span>
          </span>
          <small class="bk-id">#{{ item.backupId }}</small>
        </div>
      </template>
      <template #cell(volume)="{ value }">{{ value }}</template>
      <template #cell(openTime)="{ item }">
        <div class="bk-two">
          <span>{{ bkDateTime(item.openTime) }}</span>
          <small>@ {{ item.openPrice }}</small>
        </div>
      </template>
      <template #cell(closeTime)="{ item }">
        <div v-if="item.closeTime" class="bk-two">
          <span>{{ bkDateTime(item.closeTime) }}</span>
          <small>@ {{ item.closePrice }}</small>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(profit)="{ item }">
        <div class="bk-two text-right">
          <span class="bk-amt" :class="Number(item.profit) >= 0 ? 'bk-amt--in' : 'bk-amt--out'">{{ fmtNum(item.profit) }}</span>
          <small v-if="item.swap || item.commission">{{ $t('backup.columns.swap') }} {{ fmtNum(item.swap) }} · {{ $t('backup.columns.commission') }} {{ fmtNum(item.commission) }}</small>
        </div>
      </template>
      <template #cell(accountId)="{ value }">{{ value || '—' }}</template>
      <template #cell(clientName)="{ item }">
        <backup-client-ref :name="item.clientName" :client-backup-id="item.clientBackupId"
                           :current-client-id="item.currentClientId"
                           :client-known="'currentClientId' in item"
                           @open-workspace="id => $emit('open-client', id)"/>
      </template>
      <template #cell(state)="{ item }">
        <span v-if="item.state === 'OPEN'" class="bk-tag bk-tag--open" v-b-tooltip.hover.top="$t('backup.openTradeHint')">
          <i class="fa-solid fa-lock mr-25"></i>{{ $t('backup.viewOnly') }}
        </span>
        <span v-else class="bk-tag">{{ $t(`backup.tradeState.${item.state}`) }}</span>
      </template>
      <template #cell(matchStatus)="{ item }">
        <backup-status-badge :status="item.matchStatus" :reason="item.matchReason"
                             :current-id="item.currentId" :client-id="item.currentClientId"/>
      </template>
    </b-table>

    <table-pagination v-if="rows.length > 10" class="mt-1" :pagination="pagination"
                      :counter-label="$t('backup.tabs.trades')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <backup-bulk-bar :count="pickedCount" :label="$tc('backup.bulk.tradesSelected', pickedCount, { n: pickedCount })"
                     :record-only-hint="$t('backup.bulk.tradesHistoryHint')"
                     @clear="clearPicked" @restore="emitPlan"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBackupTable from '@/components/mixin/mixinBackupTable'
import { isRestorable, groupRestoreRequests } from '@/components/mixin/mixinBackup'
import TablePagination from '@/components/widget/TablePagination.vue'
import BackupStatusBadge from '@/components/partial/backup/BackupStatusBadge.vue'
import BackupTableState from '@/components/partial/backup/BackupTableState.vue'
import BackupBulkBar from '@/components/partial/backup/BackupBulkBar.vue'
import BackupClientRef from '@/components/partial/backup/BackupClientRef.vue'

export default {
  name: 'BackupTradesTable',
  components: { TablePagination, BackupStatusBadge, BackupTableState, BackupBulkBar, BackupClientRef },
  mixins: [mixinBackupTable],
  data: () => ({
    defaultSortField: 'openTime',
  }),
  computed: {
    fields() {
      return [
        { key: 'select', label: '', class: 'bk-col-select' },
        { key: 'symbol', label: this.$t('backup.columns.symbol'), sortable: true },
        { key: 'volume', label: this.$t('backup.columns.volume'), sortable: true },
        { key: 'openTime', label: this.$t('backup.columns.open'), sortable: true },
        { key: 'closeTime', label: this.$t('backup.columns.close'), sortable: true },
        { key: 'profit', label: this.$t('backup.columns.profit'), sortable: true, class: 'bk-col-right' },
        { key: 'accountId', label: this.$t('backup.columns.account'), sortable: true },
        { key: 'clientName', label: this.$t('backup.columns.client'), class: 'bk-col-left' },
        { key: 'state', label: this.$t('backup.columns.state'), sortable: true },
        { key: 'matchStatus', label: this.$t('backup.columns.match'), sortable: true },
      ]
    },
  },
  methods: {
    ...mapActions('backup', ['getAllBackupTrades']),
    fetchRows(params) {
      return this.getAllBackupTrades(params)
    },
    isSelectable(row) {
      return row.restorable === true && row.state === 'CLOSED' && isRestorable(row.matchStatus)
    },
    selectBlockReason(row) {
      if (this.isSelectable(row)) return ''
      if (row.state === 'OPEN') return this.$t('backup.openTradeHint')
      if (!isRestorable(row.matchStatus)) return this.$t('backup.notRestorable')
      if ('currentClientId' in row && !row.currentClientId) return this.$t('backup.restoreClientFirst')
      return this.$t('backup.tradeAccountMissing')
    },
    isBuy(row) {
      return String(row.side || '').toUpperCase().indexOf('BUY') === 0
    },
    fmtNum(v) {
      const n = Number(v) || 0
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    onRowClick(row, index, ev) {
      if (ev && ev.target && ev.target.closest && ev.target.closest('.no-click, .custom-control, a, button')) return
      if (this.isSelectable(row)) this.togglePick(row, !this.isPicked(row))
    },
    rowClass(item) {
      if (!item) return ''
      // status bar: orange = something to recover/review, green = 100% the same
      const same = item.matchStatus === 'EXISTS' || item.matchStatus === 'RESTORED'
      return ['bk-row', `bk-row--${(item.matchStatus || '').toLowerCase()}`, same ? 'bk-row--same' : 'bk-row--todo',
        this.isPicked(item) ? 'bk-row--picked' : '', this.isSelectable(item) ? 'bk-row--selectable' : '']
    },
    emitPlan() {
      const trades = this.pickedRows
      this.$emit('restore-plan', {
        source: 'trades',
        sections: { client: null, accounts: [], fields: [], monetary: [], trades, notes: [] },
        requests: groupRestoreRequests({ trades }),
      })
    },
    afterRestore() {
      this.clearPicked()
      this.refresh()
    },
  },
}
</script>
